<template>
  <div class="cardheader" :class="[zoomSelection, { 'is-news': this.view == 'timelineNews' }]">
    <div v-if="zoomSelection === 'small-view'">

      <div class="card" :title="headerText" :class="{ history: isViewHistory }" data-testid="card-header-small"> {{ headerText }} </div>

    </div>

    <div v-else class="card" :class="{ 'indicate-copied': indicateCopied }" :data-tid="cardMeta.tid"
         :data-device="cardMeta.device" :data-pollid="cardMeta.pollid"
         :data-display-date="dateTime(cardMeta.datetime)">
      <div class="card-body w-100 d-flex justify-content-between">
        <div class="d-flex flex-column w-100">
          <div class="d-flex header-data justify-content-between">
            <div class="d-flex">
              <span class="align-self-center activate-reds-and-greens">
                <!--reds and greens class is for the markup plugin to load when the popup opens-->
                <span class="icons fs-4 align-middle activate-reds-and-greens" :class="pageIconClass"
                      :data-testid="`${tidPrefix}-card-screenshot-btn`"
                      :title="isPageIconClickable ? 'Show Screenshot' : ''" @click="pageIconClickHandler" />
              </span>
              <div class="d-flex flex-column ms-2 text-left align-self-center">
                <span class="title-date">{{
                  iresults ? "iResults" : displayDate(cardMeta.datetime)
                }}</span>

                <div class="dropdown" v-if="iresults && iresultSets">
                  <span class="dropdown-toggle" id="iresultListDropdown" data-bs-toggle="dropdown"
                        data-testid="iresults-card-set-dropdown"
                        aria-expanded="false">{{
                          iresultName }}</span>
                  <ul class="dropdown-menu" aria-labelledby="iresultListDropdown">
                    <li v-for="(iresult, index) in iresultSets" :key="'iresult_option' + index">
                      <a class="dropdown-item" data-testid="iresults-card-set-listitem" @click="$emit('handleIResultSelect', iresult.id)">{{ iresult.name }}
                      </a>
                    </li>
                  </ul>
                </div>

                <span class="title-location ellipsis" v-else>{{ headerLocation }}</span>
              </div>
            </div>
            <div class="reparse" v-if="parsing || repollingNews">
              <!--WIP - see if we can implement bootstrap spinner instead of the svg with css animation-->
              <i class="icons reparse-spin"></i>
            </div>
            <i v-if="moveCardMode && !isViewHistory && isPrimaryPage"
               class="move-handle pi pi-arrow-right-arrow-left"></i>
            <div v-else>
              <div class="dropdown card-header-dropdown" v-if="
                !parsing &&
                ['timeline', 'timelineNews', 'iresults', 'parsing-tester'].includes(view) &&
                !['kp-card', 'screenshot'].includes(cardType)
              ">
                <button
                        class="dropdown-toggle p-button-outlined p-button-sm custom-dropdown-toggle clickable"
                        :data-testid="`${tidPrefix}-card-menu-btn`"
                        type="button" aria-haspopup="true" aria-expanded="false" @click="$refs.menu.toggle()">
                  <i class="pi pi-bars"></i>
                </button>
                <mini-popup ref="menu" class="profile client-search">
                  <a v-for="item in cardTypeModel" :key="item.label" class="dropdown-item"
                     href="javascript:void(0)"
                     :data-testid="`${tidPrefix}-card-menu-${item.label.toLowerCase().replace(/ /g, '-')}-btn`"
                     @click="pickMenuItem(item)">
                    <div class="d-flex">
                      <i :class="item.icon"></i> <span class="ms-2">{{ item.label }}</span>
                    </div>
                  </a>
                </mini-popup>
              </div>
            </div>
          </div>
          <div>
            <div class="term-field d-flex align-items-center">
              <img :src="logoSrc"
                   :title="isLiveSearchAvailable ? 'Live Search' : ''"
                   :class="[logoClass, { clickable: isLiveSearchAvailable }]"
                   :data-testid="`${tidPrefix}-card-livesearch-btn`"
                   @click="liveSearchHandler()" />
              <span class="term-field-title ellipsis"
                    :class="{ unset: caseSensitive, 'clickable-text': enableSuggestions }"
                    :data-testid="`${tidPrefix}-card-keyword-btn`"
                    @click="$refs.suggestions && $refs.suggestions.toggle()" :title="cardMeta.keyword">
                {{ cardMeta.keyword }}
              </span>

            </div>
            <mini-popup v-if="enableSuggestions" ref="suggestions" class="suggestion-wrapper">
              <span v-html="suggestions" />
            </mini-popup>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { unref } from "vue";
import appConfig from "@/../config/config.json";
import {
  IMPACT_QUERY_DATE_FORMAT,
  LONG_DATE_FORMAT,
  MEDIUM_DATE_FORMAT,
  SHORT_DATE_FORMAT,
} from "@/constants";
import { mapActions, mapGetters } from "vuex";

import useLiveSearchHelper from "@/composables/useLiveSearchHelper";
import { _ } from "vue-underscore";
import MiniPopup from '../modals/MiniPopup.vue';

export default {
  name: "CardHeader",
  emits: ["refreshCard", "handleIResultSelect", "pageIconSelect"],
  inject: {
    cardMetaRef: { from: 'cardMeta', default: () => ({}) }
  },
  props: {
    suggestions: String,
    isHide: Boolean,
    enableSuggestions: Boolean,
    iresults: Boolean,
    iresultSets: Array,
    iresultName: String,
    pageNumber: Number,
    cardType: String,
  },
  data() {
    return {
      parsing: false,
      repollingNews: false,
      refreshOverride: false,
      indicateCopied: false,
      dropdownId: 0,
      hotKeyPressed: false,
    };
  },
  components: {
    MiniPopup,
  },
  computed: {
    ...mapGetters("routeParams", {
      client: "client",
      view: "view",
      userInfo: "userInfo",
      ctid: "ctid",
      resultset: "resultset",
      isViewHistory: "isViewHistory",
    }),
    ...mapGetters("toolbar", {
      zoomSelection: "zoomSelection",
      caseSensitive: "caseSensitive",
      selectedPages: "selectedPages",
    }),
    ...mapGetters("timeline", { moveCardMode: "moveCardMode" }),
    cardMeta() {
      return unref(this.cardMetaRef) || {};
    },
    tidPrefix() {
      const prefixes = { timeline: "timeline", timelineNews: "timelinenews", iresults: "iresults", keywordSummary: "keywordsummary", timelineCompare: "timelinecompare" };
      return prefixes[this.view] || "timeline";
    },
    isToday() {
      if (!this.cardMeta?.datetime) {
        return false;
      }
      const dateTime = this.$dateTime.fromISO(this.cardMeta.datetime).toUTC();
      return dateTime.hasSame(this.$dateTime.now(), "day");
    },
    // Direct asset URLs on the Python API (Go /ajax removed). serveNewsHTML -> /assets/news_html,
    // serveHTML -> /assets/serp_html. Only the params the Python routes accept are kept (news_html:
    // ntid,date — serp_html: tid,date,page; Go's clientid/color params are dropped).
    // These are opened via window.open / the ScreenshotDialog iframe (direct browser navigation),
    // which CANNOT send an Authorization header — the asset routes authenticate with the HttpOnly
    // `impact_session` cookie the API sets (api.syncSessionCookie). Never put the JWT in this URL:
    // it is shared and screenshotted, and that leaked login tokens (DM-10602).
    htmlLink() {
      if (this.view === "timelineNews") {
        const date = new Date(this.cardMeta.datetimeUtc) / 1000;
        const params = new URLSearchParams({ ntid: this.cardMeta.ntid, date });
        return appConfig.pythonApiUrl + "/assets/news_html?" + params.toString();
      }
      const date = new Date(this.cardMeta.datetime) / 1000;

      const params = new URLSearchParams({
        tid: this.cardMeta.tid,
        date,
        page: this.pageNumber,
      });
      return appConfig.pythonApiUrl + "/assets/serp_html?" + params.toString();
    },
    headerText() {
      switch (this.resultset) {
        case 'geo':
          return this.headerLocation;
        case 'peergroup':
          return this.cardMeta.keyword;
        default:
          return this.displayDate(this.cardMeta.datetime);
      }
    },
    headerLocation() {
      return (
        this.cardMeta.location +
        (this.cardMeta.lang && this.cardMeta.lang !== "en"
          ? " (" + this.cardMeta.lang + ")"
          : "")
      );
    },
    pageIconClass() {
      const mobile = this.cardMeta.device === "mobile" ? "-mobile" : "";
      const page = this.pageNumber === 1 ? "one" : "two";
      return [`page-${page}${mobile}`, { clickable: this.isPageIconClickable }];
    },
    isPageIconClickable() {
      return !this.cardType;
    },
    isPrimaryPage() {
      return this.selectedPages[0] === this.pageNumber;
    },
    cardTypeModel() {
      let type = this.speedDialItems;
      switch (this.cardType) {
        case "i-results":
          type = this.speedDialIresultItems;
          break;
      }
      type = type.filter(item => !item.disabled);
      if (this.hotKeyPressed === true) {
        const hotKeyReparse = this.speedDialItems.find(item => item.label === 'Reparse');
        if (hotKeyReparse) {
          type.push(hotKeyReparse);
        }
      }
      return this.userInfo.is_su ? type : type.filter((item) => !item.suOnly);
    },
    isLiveSearchAvailable() {
      return this.cardMeta.device === "pc" && this.cardMeta.engine === "google";
    },
    // in computed instead of data because they reference computed values
    speedDialItems() {
      const items = [
        {
          label: "Screenshot",
          icon: "pi pi-external-link",
          command: () => window.open(this.htmlLink, "_blank"),
        },
        {
          label: "Hide",
          icon: "pi pi-times",
          command: () => this.toggleHideCardById(this.cardMeta.pollid),
          disabled: !["timeline", "timelineNews"].includes(this.view),
        },
        {
          label: "History",
          icon: "bi bi-clock-history",
          command: () => this.setActionGetHistory(this.cardMeta.tid),
        },
        {
          label: "Copy",
          icon: "bi bi-file-earmark",
          command: () => this.$parent.$emit("showCopyCardOptions"),
          disabled: !this.isViewHistory || this.isCopied || !["timeline", "timelineNews"].includes(this.view),
        },
        {
          label: "Reparse",
          icon: "pi pi-refresh",
          command: () => this.initReparse(this.cardMeta.pollid, this.client.clientid),
          disabled: !this.cardMeta.reparse || this.isCopied || this.view === "timelineNews",
        },
        {
          label: "Repoll",
          icon: "icons repoll",
          command: () => this.repollNews(),
          disabled: !(this.view === "timelineNews" && this.isToday),
          suOnly: true,
        },
        {
          label: "AI Analyze",
          icon: "icons ai",
          command: () => this.analyzeNewsCard(),
          disabled: !(this.view === "timelineNews" && !this.isCopied),
        },
      ];
      return items;
    },
    speedDialIresultItems() {
      return [
        {
          label: "Open",
          icon: "iresult i-open",
          command: () => this.setIResultToolbarAction("open"),
        },
        {
          label: "Undo",
          icon: "iresult i-undo",
          command: () => this.setIResultToolbarAction("undo"),
        },
        {
          label: "Delete",
          icon: "iresult i-delete",
          command: () => this.setIResultToolbarAction("delete"),
        },
        {
          label: "Rename",
          icon: "iresult i-rename",
          command: () => this.setIResultToolbarAction("rename"),
        },
        {
          label: "Save As",
          icon: "iresult i-save-as",
          command: () => this.setIResultToolbarAction("saveAs"),
        },
        {
          label: "Save",
          icon: "iresult i-save",
          command: () => this.setIResultToolbarAction("save"),
        },
        {
          label: "New",
          icon: "iresult i-new",
          command: () => this.setIResultToolbarAction("new"),
        },
      ];
    },
    isCopied() {
      return this.cardMeta?.pollid?.includes("copied");
    },
    logoClass() {
      const value = this.cardMeta.engine || this.cardMeta.device;
      if (!value) {
        return;
      }

      return ["logo", "logo-" + value, "logo-" + this.zoomSelection];
    },
    logoSrc() {
      // DM-10523: show the Google logo for both pc and mobile google cards
      // (the logo is engine-specific, not device-specific).
      if (this.cardMeta.engine === 'google') {
        if (this.view === 'timelineNews') {
          return 'data:image/svg+xml,<%3Fxml version="1.0" encoding="UTF-8"%3F><svg version="1.1" viewBox="0 0 6550.8 5359.7" xmlns="http://www.w3.org/2000/svg"><path d="M5210.8 3635.7c0 91.2-75.2 165.9-167.1 165.9H1507c-91.9 0-167.1-74.7-167.1-165.9V165.9C1339.9 74.7 1415.1 0 1507 0h3536.8c91.9 0 167.1 74.7 167.1 165.9v3469.8z" fill="%230C9D58"/><polygon points="5210.8 892 3885.3 721.4 5210.8 1077" fill="%23004D40" opacity=".2"/><path d="M3339.3 180.9L1332 1077.2l2218.5-807.5v-2.2c-39-83.6-134-122.6-211.2-86.6z" fill="%23004D40" opacity=".2"/><path d="M5043.8 0H1507c-91.9 0-167.1 74.7-167.1 165.9v37.2c0-91.2 75.2-165.9 167.1-165.9h3536.8c91.9 0 167.1 74.7 167.1 165.9v-37.2C5210.8 74.7 5135.7 0 5043.8 0z" fill="%23FFFFFF" opacity=".2"/><path d="m2198.2 3529.1c-23.9 89.1 23.8 180 106 202l3275.8 881c82.2 22 169-32.9 192.8-122l771.7-2880c23.9-89.1-23.8-180-106-202l-3275.8-881c-82.2-22-169 32.9-192.8 122l-771.7 2880z" fill="%23EA4335"/><polygon points="5806.4 2638.1 5978.7 3684.8 5806.4 4328.1" fill="%233E2723" opacity=".2"/><polygon points="3900.8 764.1 4055.2 805.6 4151 1451.6" fill="%233E2723" opacity=".2"/><path d="m6438.6 1408.1-3275.8-881c-82.2-22-169 32.9-192.8 122l-771.7 2880c-1.3 4.8-1.6 9.7-2.5 14.5l765.9-2858.2c23.9-89.1 110.7-144 192.8-122l3275.8 881c77.7 20.8 123.8 103.3 108.5 187.6l5.9-21.9c23.8-89.1-23.9-180-106.1-202z" fill="%23FFFFFF" opacity=".2"/><path d="M4778.1 3174.4c31.5 86.7-8.1 181.4-88 210.5L1233.4 4643c-80 29.1-171.2-18-202.7-104.7L10.9 1736.5c-31.5-86.7 8.1-181.4 88-210.5L3555.6 267.9c80-29.1 171.2 18 202.7 104.7l1019.8 2801.8z" fill="%23FFC107"/><path d="M24 1771.8c-31.5-86.7 8.1-181.4 88-210.5L3568.7 303.1c79.1-28.8 169 17.1 201.5 102l-11.9-32.6c-31.6-86.7-122.8-133.8-202.7-104.7L98.9 1526c-80 29.1-119.6 123.8-88 210.5l1019.8 2801.8c.3.9.9 1.7 1.3 2.7L24 1771.8z" fill="%23FFFFFF" opacity=".2"/><path d="M5806.4 5192.2c0 92.1-75.4 167.5-167.5 167.5h-4727c-92.1 0-167.5-75.4-167.5-167.5V1619.1c0-92.1 75.4-167.5 167.5-167.5h4727c92.1 0 167.5 75.4 167.5 167.5v3573.1z" fill="%234285F4"/><path d="m4903.8 2866h-1414.4v-372.2h1414.4c41.1 0 74.4 33.3 74.4 74.4v223.3c0 41.1-33.3 74.5-74.4 74.5zm0 1414.3h-1414.4v-372.2h1414.4c41.1 0 74.4 33.3 74.4 74.4v223.3c0 41.2-33.3 74.5-74.4 74.5zm223.3-707.2h-1637.7v-372.2h1637.7c41.1 0 74.4 33.3 74.4 74.4v223.3c0 41.2-33.3 74.5-74.4 74.5z" fill="%23FFFFFF"/><path d="M5638.9 5322.5h-4727c-92.1 0-167.5-75.4-167.5-167.5v37.2c0 92.1 75.4 167.5 167.5 167.5h4727c92.1 0 167.5-75.4 167.5-167.5V5155c0 92.1-75.4 167.5-167.5 167.5z" fill="%231A237E" opacity=".2"/><path d="m911.9 1488.8h4727c92.1 0 167.5 75.4 167.5 167.5v-37.2c0-92.1-75.4-167.5-167.5-167.5h-4727c-92.1 0-167.5 75.4-167.5 167.5v37.2c0-92.1 75.4-167.5 167.5-167.5z" fill="%23FFFFFF" opacity=".2"/><path d="M2223.9 3238.2v335.7h481.7c-39.8 204.5-219.6 352.8-481.7 352.8-292.4 0-529.5-247.3-529.5-539.7s237.1-539.7 529.5-539.7c131.7 0 249.6 45.3 342.7 134v.2l254.9-254.9c-154.8-144.3-356.7-232.8-597.7-232.8-493.3 0-893.3 399.9-893.3 893.3s399.9 893.3 893.3 893.3c515.9 0 855.3-362.7 855.3-873 0-58.5-5.4-114.9-14.1-169.2h-841.1z" fill="%23FFFFFF"/><g fill="%231A237E" opacity=".2"><path d="m2233.2 3573.9v37.2h472.7c3.5-12.2 6.5-24.6 9-37.2h-481.7z"/><path d="M2233.2 4280.3c-487.1 0-882.9-389.9-892.8-874.7-.1 6.2-.5 12.4-.5 18.6 0 493.4 399.9 893.3 893.3 893.3 515.9 0 855.3-362.7 855.3-873 0-4.1-.5-7.9-.5-12-11.1 497-347.4 847.8-854.8 847.8zM2575.9 2981.3c-93.1-88.6-211.1-134-342.7-134-292.4 0-529.5 247.3-529.5 539.7 0 6.3.7 12.4.9 18.6 9.9-284.2 242.4-521.1 528.6-521.1 131.7 0 249.6 45.3 342.7 134v.2l273.5-273.5c-6.4-6-13.5-11.3-20.1-17.1L2576 2981.5l-.1-.2z"/></g><path d="M4978.2 2828.7v-37.2c0 41.1-33.3 74.4-74.4 74.4H3489.4v37.2h1414.4c41.1.1 74.4-33.2 74.4-74.4zM4903.8 4280.3H3489.4v37.2h1414.4c41.1 0 74.4-33.3 74.4-74.4v-37.2c0 41.1-33.3 74.4-74.4 74.4zM5127.1 3573.1H3489.4v37.2h1637.7c41.1 0 74.4-33.3 74.4-74.4v-37.2c0 41.1-33.3 74.4-74.4 74.4z" fill="%231A237E" opacity=".2"/><path d="M6438.6 1408.1l-1227.7-330.2v-912c0-91.2-75.2-165.9-167.1-165.9H1507c-91.9 0-167.1 74.7-167.1 165.9v908.4L98.9 1526c-80 29.1-119.6 123.8-88 210.5l733.5 2015.4v1440.3c0 92.1 75.4 167.5 167.5 167.5h4727c92.1 0 167.5-75.4 167.5-167.5v-826.9l738.3-2755.2c23.8-89.1-23.9-180-106.1-202z" fill="url(%23a)"/></svg>';
        }
        return "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAAZCAYAAADE6YVjAAACsElEQVR42mNgIAHcdzAQeO5goPDa1VDqvwMDCwM1AMigV07Goa8cTVa/dDJ5+crZ9D8MA/m/XjubnAXSLS9sdJXIsuClk1HgS2eTe8gG48G/gWpnvgf6lCjDzxgbs4I0EGn4fzTf3X/rbKiFP3gYGJiBwbOOHAsglphefemsI04giIx7cBnwwsn4AlC++5WjcR7QIRVAVy8HGvqJNAscTaxfOZv8xbDAyeT2C0dDZ2x6HtroCr52NJlBlAUg8LlD/PArN2M07xuf/gA0iGAqtLTkJGjBr10MBr/3sP7/voTv/5sgPVgkvnvjpC/NQC3wezdLA8gSEP65meP/uzQ1kC/KGagJfu1h2QGzBIx3s/790iqGNYztmr4aOrV+Xk8Mdmj+2oDkE9abyJb82s16F5eDHFu/uDu1fvlPJL6KsGQPyxMUn+xhOU0VS1q+PKG5TxxbvtzHGSc/gXEye6Mi1jixb/pk69j6+TY2DDT4L5pFJ7Gmrte7uP9nrLX9b7w8kKTUZV3/VQroy39oPlmAkU/Obxf977XK87/JiuD/xiuC3ukv9CE6nzg1f25BDy775o+JKIoWbVY/bLkiEGwBDBuvCD6tu9SbYI4HBaFT69cfqL74/M2y/qMQikKTFUHWQEP/IlsCtei20bJAJ6ym73dgMV2cmuXQ8fQLui8cmj9PwarHeHlQD7olcLw86DxQvhsYV3lGy4LKjJYHzzVeHvwMJGe6OOW/Xc8FZEteONR/EsHu71WhzMC4WI/TInx4edh/68nrgRZ8/mHf+NkRb/gazzRmBbp2FlkWrQj6aDFtsifRSdJoeVAgMHjuEmM40Pf/gGq3G68IUCa95ARGrPGKwFCj5YFrgIa8RDP8D9Dwa8D4mWywOMiUaqW1wfwAAYPlAQr6q3ykteq12IjVBwDAMv42sa57lQAAAABJRU5ErkJggg==";
      } else if (this.cardMeta.engine === 'bing' || this.cardMeta.device === 'bingpc') {
        return "data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 23 23'%3e%3cpath fill='%23f3f3f3' d='M0 0h23v23H0z'/%3e%3cpath fill='%23f35325' d='M1 1h10v10H1z'/%3e%3cpath fill='%2381bc06' d='M12 1h10v10H12z'/%3e%3cpath fill='%2305a6f0' d='M1 12h10v10H1z'/%3e%3cpath fill='%23ffba08' d='M12 12h10v10H12z'/%3e%3c/svg%3e"
      } else if (this.cardMeta.engine === 'baidu') {
        return "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAAZCAIAAABLixI0AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAASiSURBVEhLfZV7TFtVHMf7lyayIY/xaEvLo6UUmGODoRsbaDDqnLqni8hmJkjoHDKd4l+KrxAjM+rYhtFMM6cTMwgzI8HMxelCXFzUZSEKGug99/ZSoE9KH7SUtb1+T89dQdP4y+ePm9+953vO+b2uQkpmf//uLK8Wyzdavh0KyC7Zln74zjk8MHMruCg7VlgSLX8g9uATM4VlvKFCSFWSmMcD4m9CLU0kp0jI0Qk1tZwU88Wdy5ZE6+KgM0NDSistQF3Mv/muB8B/8pN5lZ4vWS8YNwjZheTc+QX2fcKSaA2cnlTqBaaVX8q3vmAH8FdsEvX3yH5tqfB8K8GV48iWRGto0JFVwMtrjPyLrzoB/HdmmJnTuMGiKeHfentWkqJxZINW1OVY+vkisQiLAK6JiUVjBUGwcJdVueT6ryEAf9UWUb+Ongtaa7Tc+J/BuMKyKbzu0KY6IVUtaA1mMNA/J0mx97odd6SZ0/N4UzMnfyhJXcfmUpVcQRmfqSW1D02vvB0zhandoTHwOAIOAnAR9kIQI16rmz3ftmDPCdeTB2bPnsGVI+Zxb+cbNtB21Dk6ijxEFRX3WZAaFgig1JFPP/fKa//HYj6N0YK0gjwDryoyc5NBRSI1jLxi/tiHrJqYhaVoQGaFnR8MZOfLdQMKSoWDrXbF/dusxfGIMjI15NJlP1sgjjn3NVq3P0bAw9tIfx8qI8JefdDjwa6JVQgRmkRx5pw3p5Do1graEh6UVomSRI8w8dusWk+0RqForUyamjSbUAe38Pajk//SQpTurRNpfXV2uatrxUd2TIMJc5juK0mVm4V4OAj2BPHNaRv0vi/g7YmP55VFy3fEUR7fO52kVmHtrzhxWVO7fWg4wA7F5JBotUFg5bZGS3AhgDJAz/X1+5JoPdNiw/440Zdf0+7VGHmQyDWCW1Qu/Hh1weuLfNXnA0c6HJev0Lr9r1bTITsKMk3FKVImERR4cnU8SFdzKh29FOZHSrY5I49LRCNhspbf7t1abwF3ZXGdr6Oml626bgrgYfj7ACSaTLTP2zscGXnkyk8LgGUDJmu1dzgxRsCje2jH3hwN73xqpqeXnquyRgShUGzggh9a+5ttcB4+alfrecM6ys0bcg1RrdFfHCk5RFUsgP7TdJLU1JF0NWk5TJcltPov+HDT/U1xrZfsGEeF5QJYvxllRI1qvfaOG7lAgsDZXqT81o5dmKjcsya6rGqrCBYXY98M+NDwexpwcFmLZQOz18XToUS1uo/P48BoJlD9AIIVdnmkz77wDl+iRcsW+P1R11z0+CkP+wO0vexIaKVreNu4Vdayc64MLa0ggCG3c5/1xojN5485XZFgMMoaePvuaQdxz/tiLnfE6402HbLhdigUgCwvhelElGPf2ICfAg9QjfgIc/VuJbc6l1uVw6FXgFLHr1YSOBH+TA2Hb/BldiHlSJuFicha6MGDz82AsiqLSkcrs6beums32dsw1dU9B1ra7A0HprbUW4orBNR6Vj4p3yg2Ps0D1r+whJZsk+ala9dDY3+FXe6oFEM1L49zPLtdS3+MhUeuBa+OBDmCssJMv/2jlKR/AP9xD9/7wqzfAAAAAElFTkSuQmCC"
      } else if (this.cardMeta.engine === 'so') {
        return "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAAZCAYAAADE6YVjAAADlElEQVR42t1WXWgUVxS+bSOavzU7v5tEDbYGU2LzEkJpQSoIDWZj4s7s4kPRBx8KvgXtQ0WKID60tPQXrIFKoKI1S5LdnZk7MyuFoC1t00ZFEbGtD6GpLQ01dpPdmcnv7blpdjKzSYzZBx86cNjlnnO+73znnDu7CP2vn/JUhyhg+VjIjN4Cy4TM2FwoHZ1fMDNKv/8Dn0OCKb3DKu0N62f4ei8rYulD0ZRHKGh1OkZWMyByBFO+yuvSQch85onwq3BbnWhEb64ImFeyCjGvySfR4J6SxxKU9bdWC7r0XSFICBQJZiwBFZ+FAj4S0/IXoPIbUDFZoMrioH0A9ezKDPq+gGBI170ENAl6fpqqQ0pzmduOOHouEG9lhHTnq4Ihf1ug1mZN6ehygsFYhahHThYQ/MljWV6zvYkDVaCwpyB3hNUiL/oCYWhR7xwgaGZhkGQ12f6ncmAvC4oNj5oZUYf5eB8gUEPp2Gx+uLSydW/k+Z2VMLtxT6HDPj8c3PcMOcMqUksxd0swo597SKYqBl4XXGe1GX3krYBLdtQURWJIh6rN2FQeCzY14lWSyztEQ76y+WI4WAwJrG8Y2p1xsUypa4nEWNp3SkI3phgS3pDb6CvIVYIPHHOdtUrkwY7+/aT5Qitp7Av/VAeXshiS59WON5r62pxdvWFSiyVCt9Z1Rr96bej85UYymqohPwxsn0gkXmguhkRJ1J+9l9xKbgxsI4fju2dL4+21rnMose29MS007+gCAZvPqdy59RIMv4k25DD3B8WwMU+uJrf/6gv4G7MvT2r87UUS4mB+JqeyEXLqyS4j0ZmArTK9i7lg3Oy4wr/rDxpEJZbKnnB0Pq+G2Br3u6MFw2sRjMVRhaOynwDw3H8EPLFVbjSbCjYtCx6Po822xt7xEWE+a6nccZIsqyE9aJO/ehSYSpa/ZGMOuzmUROOmLSX4No3JxlHIUQP1mThi3ES7f1MdKPjRS0TNwtwvYL22xrxvY/aMpQY/szT+CsQ98sYBiQMkH9DOZGkBGnuXnmU17ppP0UQf22Br/M++ZNcoed6W+Qi07WNQuJHiWCmmC1o4v3AO8ctal72EREdnPwVVoysD+qqftnXu+5zGHOnuRhvyGDkl0GJh9jfAmLNU/vaqK+kYlTsthXkL+j5MwbzgAPwQWtY/gas6/0qVi4hu4pdN5ahnz8LsCPzAZfo21lsq84qll255Kv9+/gVlxpWSX8DbrwAAAABJRU5ErkJggg=="
      } else {
        return '';
      }
    },
  },
  created() {
    window.addEventListener("keydown", (e) => {
      if (e.key == ";") {
        this.refreshOverride = !this.refreshOverride;
      }
      if (e.shiftKey && e.key == ":") {
        this.hotKeyPressed = !this.hotKeyPressed;
        this.cardTypeModel;
        this.reparseToggle();
      }
    });
  },
  mounted() {
    this.indicateCopied = this.isCopied;
    window.setTimeout(() => (this.indicateCopied = false), 5000);
  },

  methods: {
    ...mapActions("timeline", ["toggleHideCardById"]),
    ...mapActions("routeParams", ["setCtid", "setResultset", "setStart", "setEnd", "setCtidAndResultset"]),
    ...mapActions("iresults", ["setIResultToolbarAction"]),
    ...mapActions("markup", ["addAiAnalysisPollUrls"]),

    displayDate(value) {
      const fString = {
        "regular-view": LONG_DATE_FORMAT,
        "medium-view": MEDIUM_DATE_FORMAT,
        "small-view": SHORT_DATE_FORMAT,
      }[this.zoomSelection];

      return this.$dateTime.fromISO(value).toUTC().toFormat(fString);
    },
    dateTime(date) {
      const dt = this.$dateTime.fromISO(date).toUTC().startOf("day");
      return dt.toSeconds();
    },
    setActionGetHistory(tid) {
      this.$api.getCtid(this.client.clientid, tid).then((result) => {
        // set last 30 days
        const now = this.$dateTime.now();
        const startDate = now.minus({ days: 30 }).toFormat(IMPACT_QUERY_DATE_FORMAT);
        const endDate = now.toFormat(IMPACT_QUERY_DATE_FORMAT);
        this.setStart(startDate);
        this.setEnd(endDate);

        this.setCtidAndResultset({ ctid: result.data, resultset: "history" });
      });
    },
    initReparse(googlepollid, clientid) {
      if (!this.parsing) {
        this.parsing = true;
        // The third arg (refetch) is used by the reparse flow to re-fetch this card's timeline
        // after polling the Python API for completion.
        const date = this.$dateTime.fromISO(this.cardMeta.datetime).toUTC().toFormat(IMPACT_QUERY_DATE_FORMAT);
        this.$api
          .reparse(googlepollid, clientid, { ctid: this.ctid, date, resultset: this.resultset })
          .then((res) => {
            if (res.data) {
              this.parsing = false;
              this.$parent.$emit("refreshCard", res.data);
            }
          })
          .catch((e) => {
            this.parsing = false;
            this.$notifier.error({ message: "Reparse failed", e });
          });
      }
    },
    async analyzeNewsCard() {
      const dateObj = this.$dateTime.fromISO(this.cardMeta.datetime).toUTC();
      const dateText = dateObj.toFormat(SHORT_DATE_FORMAT);
      this.$notifier.info({ message: `Analyzing results for ${dateText}` });

      try {
        const response = await this.$api.analyzeNewsCard(this.client.clientid, this.cardMeta.ntid, dateObj.startOf("day"), this.cardMeta.location, this.cardMeta.country, this.cardMeta.country);
        const urlids = response.data[0]?.urlids;
        this.addAiAnalysisPollUrls(urlids);
      } catch (error) {
        this.$notifier.error({ message: "An error occurred starting AI analysis", error });
      }
    },

    repollNews() {
      if (this.repollingNews) {
        return;
      }

      this.repollingNews = true;
      this.$api.repollNews(this.cardMeta.ntid)
        .then((res) => {
          this.$notifier.info({ message: "Repoll started. Refresh the page in a few minutes to see the updated card." })
          if (res.data) {
            // this.$parent.$emit("refreshCard", res.data);
            // TODO: refresh with results
          }
        })
        .catch((e) => {
          this.$notifier.error({ message: "Repoll failed", e });
        })
        .finally(() => this.repollingNews = false);
    },
    reparseToggle() {
      const item = _.find(this.speedDialItems, (sdItem) => sdItem.label === "reparse");

      if (item) {
        item.disabled = !this.cardMeta.reparse && !this.refreshOverride;
      }
    },
    liveSearchHandler() {
      if (this.isLiveSearchAvailable) {
        const h = this.cardMeta;
        this.openSearch(h.keyword, h.location, h.country, h.lang, this.view);
      }
    },
    pageIconClickHandler() {
      if (this.isPageIconClickable) {
        this.$emit("pageIconSelect", this.htmlLink);
      }
    },
    pickMenuItem(item) {
      this.$refs.menu.toggle();
      item.command && item.command();
    },
  },
  setup() {
    const { openSearch } = useLiveSearchHelper();
    return {
      openSearch,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_icons.scss";
@import "@/scss/_mixins.scss";

.cardheader.is-news>div {
  @include bg-newsprint();

  .title-date,
  .title-location {
    text-shadow: 1px 1px white;
  }
}

.clickable {
  @include hover-clickable-effect;
}

.clickable-text {
  @include hover-clickable-text-effect;
}

.icons.ai {
  width: 19px;
  height: 19px;
  filter: grayscale(1);
}

.small-view .card {
  --timeline-small-view-header-box-margin: 2px;

  overflow: hidden;
  font-size: var(--timeline-card-header-font-size-small);
  margin: var(--timeline-small-view-header-box-margin);
  width: calc(var(--timeline-card-width-small) + (var(--timeline-result-margin-small) * 2) - (var(--timeline-small-view-header-box-margin) * 2));
  height: 2.75rem;
  color: var(--fb-blue-70);
  text-align: center;
  border: none;

  &.history {
    font-weight: bold;
    height: 1.5rem;
  }
}

.title-date {
  color: var(--fb-blue-70);
  white-space: nowrap; // for image copies - DM-3876
}

.regular-view {
  .card {
    margin: var(--timeline-result-margin-regular);
    font-size: var(--timeline-card-header-font-size-regular);
    padding: 0.5rem;
  }

  .term-field {
    height: 36px;
    margin-top: 0.5rem;

    .logo-regular-view {
      width: 1.125rem;
      margin-left: .3rem;
    }
  }

  .title-location {
    font-size: var(--timeline-card-header-font-size-medium);
  }

  .suggestion-wrapper :deep(ul) li {
    font-size: var(--timeline-card-header-font-size-regular);
  }
}

.medium-view {
  .card {
    margin: var(--timeline-result-margin-medium);
    font-size: var(--timeline-card-header-font-size-medium);
    padding: 0.2rem;
  }

  .term-field {
    height: 32px;
    margin-top: 0.5rem;
    margin-bottom: 0.25rem;
    line-height: 14px;

    .logo-medium-view {
      width: .9rem;
      margin-left: .3rem;
    }
  }

  .title-location {
    font-size: var(--timeline-card-header-location-font-size-medium);

    &.ellipsis {
      @include ellipsis;
      max-width: var(--result-text-width-card-title-medium);
    }
  }

  .suggestion-wrapper :deep(ul) li {
    font-size: var(--timeline-card-header-font-size-medium);
  }
}

.regular-view,
.medium-view {
  .move-handle {
    color: var(--fb-blue-70);
    cursor: move;
  }

  .card {
    background: #ffffff;
    border-radius: var(--border-radius-card);
    margin-bottom: 0;
    border: 0;
    transition: box-shadow 1s ease-in-out;

    &.indicate-copied {
      box-shadow: 0 0 10px var(--fb-blue-70);
    }

    .card-body {
      border-radius: var(--border-radius-card);
      padding: 0 3px;

      .title-location {
        font-weight: 500;
        line-height: 14px;
        color: var(--text-color-secondary);
      }

      i,
      svg {
        &.document-icon {
          color: var(--timeline-toolbar-selected-color);
          position: relative;
        }

        &.fa-mobile-alt {
          top: 4px;
        }
      }

      .reparse {
        position: absolute;
        right: 14px;
        top: 4px;
        color: var(--timeline-toolbar-selected-color);
        font-size: 16px;
        margin: auto 0;

        i {
          opacity: 0.8;
        }

        .reparse-spin {
          color: var(--timeline-toolbar-selected-color);
          animation: spin 2s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }

          100% {
            transform: rotate(360deg);
          }
        }
      }

      .header-data {
        height: 30px;
      }

      .dropdown {
        font-style: normal;
        font-weight: 400;
        font-size: 13px;
        line-height: 16px;
        color: var(--fb-gray-70);
      }

      .dropdown {
        .dropdown-toggle {
          cursor: pointer;
        }

        .dropdown-menu.show {
          max-height: 20rem;
          overflow: auto;
          cursor: pointer;
        }

        li {
          font-size: 0.75rem;
        }
      }

      .term-field {
        border: 1px solid #dce3ef;
        box-sizing: border-box;
        border-radius: 4px;
        overflow: hidden;
        background: white;

        .term-field-title {
          text-transform: capitalize;
          display: inline-block;
          vertical-align: middle;
          max-height: 32px;
          cursor: pointer;
          color: var(--fb-gray-70);
          margin-left: 2px;

          &.unset {
            text-transform: unset;
          }
        }

        .icon-link {
          margin-left: auto;
          text-decoration: none;
          display: flex;
          align-items: center;
          padding: 10px;
          color: var(--icon-link-color);
        }

        img {
          margin-right: 5px;
        }
      }

      .suggestion-wrapper {
        min-width: 200px;
        padding-left: 0.5rem;

        :deep(ul) {
          list-style-type: none;
          padding-left: 0;
          margin-bottom: 0;
          display: block;

          li {
            display: list-item;
            color: var(--fb-gray-70);
          }
        }
      }
    }

    .second-page-title {
      font-size: 13px;
      font-weight: 400;
      line-height: 16px;
      color: var(--timeline-toolbar-selected-color);
      text-align: center;
      padding: 7px;
    }
  }
}

.card-header-dropdown {
  .custom-dropdown-toggle {
    background: transparent;
    color: var(--fb-gray-70);
    padding: 0px 0px 0 6px;
    border: 0;

    &:after {
      display: none;
    }

    &:focus,
    &:focus-visible,
    &:hover,
    &:active,
    &.active {
      background-color: transparent !important;
      border-color: transparent !important;
      color: var(--fb-gray-70) !important;
      outline: 0;
    }
  }

  .dropdown-menu {
    --bs-dropdown-link-active-bg: transparent !important;
  }

  .dropdown-menu {
    .dropdown-item {
      padding: 0.5rem 1rem;
      font-size: 0.95rem;
      color: var(--fb-gray-70);
    }
  }
}
</style>
